import { useContext, createContext, useState, useEffect, ReactNode } from 'react';
import { type SessionUser, UserRole } from '@/types/auth';

/**
 * Session provider over the suite's shared identity.
 *
 * The hub (`ortam`, :4040) owns sign-in for every application under it: `/api/auth/*`
 * is proxied to it by the dev server, so the `ortam_session` cookie is issued for
 * `localhost` and one sign-in covers the whole suite. The JWT also lands in
 * localStorage as `auth_token` — the bearer every pooled drive call re-sends.
 */
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: SessionUser | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface HubUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AuthPayload {
  token: string;
  user: HubUser;
}

function toSessionUser(hub: HubUser, token: string): SessionUser {
  // The hub speaks MEMBER/MODERATOR/ADMIN; this UI only distinguishes ADMIN.
  const role = String(hub.role).toUpperCase() === UserRole.ADMIN ? UserRole.ADMIN : UserRole.USER;
  return {
    id: hub.id,
    name: hub.name,
    email: hub.email,
    role,
    accessToken: token,
    token,
    storageUsed: 0,
    storageLimit: 0,
  };
}

async function authPost(path: string, body: unknown): Promise<AuthPayload> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string; message?: string }).error
      || (data as { message?: string }).message
      || `Request failed (${res.status})`);
  }
  return data as AuthPayload;
}

function persist(session: SessionUser) {
  localStorage.setItem('auth_token', session.accessToken);
  localStorage.setItem('auth_user', JSON.stringify(session));
}

function clearPersisted() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem('auth_token');
      try {
        // The cookie rides along on the same-origin proxy; the bearer covers the
        // standalone case where the cookie was never issued.
        const res = await fetch('/api/auth/status', {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = (await res.json().catch(() => ({}))) as { user?: HubUser | null };
        if (cancelled) return;
        if (data.user) {
          // A cookie-restored session may hold no bearer of its own (signed in on
          // another port), and a cached one may be stale; the drive's pooled calls
          // cross an origin the cookie does not follow, so always carry a fresh one.
          let bearer = '';
          try {
            const tokenRes = await fetch('/api/auth/token', { credentials: 'include' });
            if (tokenRes.ok) {
              bearer = ((await tokenRes.json()) as { token?: string }).token || '';
            }
          } catch {
            /* fall back to the cached token below */
          }
          const session = toSessionUser(data.user, bearer || token || '');
          if (session.accessToken) persist(session);
          setUser(session);
          setIsAuthenticated(true);
        } else {
          clearPersisted();
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch {
        // Hub unreachable: the cached sign-in stays the honest answer offline.
        const userData = localStorage.getItem('auth_user');
        if (token && userData) {
          try {
            setUser(JSON.parse(userData) as SessionUser);
            setIsAuthenticated(true);
          } catch {
            clearPersisted();
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const data = await authPost('/api/auth/login', { email, password });
    const session = toSessionUser(data.user, data.token);
    persist(session);
    setIsAuthenticated(true);
    setUser(session);
  };

  const register = async (name: string, email: string, password: string) => {
    const data = await authPost('/api/auth/register', { name, email, password });
    const session = toSessionUser(data.user, data.token);
    persist(session);
    setIsAuthenticated(true);
    setUser(session);
  };

  const logout = async () => {
    clearPersisted();
    setIsAuthenticated(false);
    setUser(null);
    // The cookie is shared by every port, so signing out has to clear it at the hub —
    // dropping only localStorage would leave the sibling applications signed in.
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      /* local sign-out stands even if the hub is unreachable */
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
